import React from 'react';
import { useTranslation } from 'react-i18next';
import { formatCurrency } from '@/utils/helpers';
import type { AdminProductRow } from '../productManagement';
import ProductImage from './ProductImage';
import ProductStockBadge from './ProductStockBadge';

interface AdminProductMobileListProps {
  rows: AdminProductRow[];
  isInitialLoading: boolean;
  error: string;
  hasActiveFilters: boolean;
  onRetry: () => void;
  onClearFilters: () => void;
}

const AdminProductMobileList: React.FC<AdminProductMobileListProps> = ({
  rows,
  isInitialLoading,
  error,
  hasActiveFilters,
  onRetry,
  onClearFilters,
}) => {
  const { t } = useTranslation();

  if (isInitialLoading) {
    return (
      <div className="space-y-3 p-4" role="status" aria-label={t('admin.products.loading')}>
        {Array.from({ length: 5 }, (_, index) => (
          <div key={index} className="h-28 animate-pulse rounded-xl bg-surface-container-low" />
        ))}
      </div>
    );
  }

  if (error && rows.length === 0) {
    return (
      <div className="flex min-h-[300px] flex-col items-center justify-center px-4 py-12 text-center" role="alert">
        <span className="material-symbols-outlined text-4xl text-error">cloud_off</span>
        <p className="mt-2 font-medium text-on-surface">{t('admin.products.error.title')}</p>
        <p className="mt-1 text-body-sm text-error">{error}</p>
        <button type="button" className="mt-3 rounded-lg bg-primary px-4 py-2 font-label-md text-on-primary" onClick={onRetry}>
          {t('admin.products.actions.retry')}
        </button>
      </div>
    );
  }

  if (rows.length === 0) {
    return (
      <div className="flex min-h-[300px] flex-col items-center justify-center px-4 py-12 text-center text-secondary">
        <span className="material-symbols-outlined text-4xl text-outline">inventory_2</span>
        <p className="mt-2 font-medium text-on-surface">{t(hasActiveFilters ? 'admin.products.empty.filtered_title' : 'admin.products.empty.title')}</p>
        <p className="mt-1 text-body-sm">{t(hasActiveFilters ? 'admin.products.empty.filtered_description' : 'admin.products.empty.description')}</p>
        {hasActiveFilters && (
          <button type="button" className="mt-3 rounded-lg border border-outline-variant px-4 py-2 font-label-md text-primary" onClick={onClearFilters}>
            {t('admin.products.actions.clear_filters')}
          </button>
        )}
      </div>
    );
  }

  return (
    <div className="divide-y divide-outline-variant/60">
      {rows.map(({ product, variant, cost, quantity, status }) => (
        <article key={`${product.id}-${variant.id}`} className="p-4">
          <div className="flex items-start gap-3">
            <input type="checkbox" aria-label={t('admin.products.table.select_product', { name: product.name })} className="mt-1 h-4 w-4 shrink-0" />
            <ProductImage name={product.name} className="h-14 w-14 rounded-xl" />
            <div className="min-w-0 flex-1">
              <div className="flex flex-col items-start justify-between gap-2 sm:flex-row">
                <div className="min-w-0">
                  <h3 className="truncate font-medium text-on-surface">{product.name}</h3>
                  <p className="mt-0.5 truncate text-[12px] text-secondary">{variant.versionName} · {variant.color}</p>
                </div>
                <ProductStockBadge status={status} />
              </div>
            </div>
          </div>

          <dl className="mt-3 grid grid-cols-2 gap-x-4 gap-y-2 text-[12px] sm:grid-cols-3">
            <div className="min-w-0"><dt className="text-secondary">{t('admin.products.table.sku')}</dt><dd className="truncate font-mono text-on-surface">{variant.id > 0 ? variant.id : t('admin.products.table.automatic')}</dd></div>
            <div className="min-w-0"><dt className="text-secondary">{t('admin.products.table.stock')}</dt><dd className="font-mono font-semibold text-on-surface">{quantity}</dd></div>
            <div className="min-w-0"><dt className="text-secondary">{t('admin.products.table.category')}</dt><dd className="truncate text-on-surface">{product.category?.name || '—'}</dd></div>
            <div className="min-w-0"><dt className="text-secondary">{t('admin.products.table.brand')}</dt><dd className="truncate text-on-surface">{product.brand?.name || '—'}</dd></div>
            <div className="min-w-0"><dt className="text-secondary">{t('admin.products.table.cost')}</dt><dd className="font-mono text-on-surface">{cost === null ? '—' : formatCurrency(cost)}</dd></div>
            <div className="min-w-0"><dt className="text-secondary">{t('admin.products.table.price')}</dt><dd className="font-mono font-medium text-on-surface">{formatCurrency(variant.price)}</dd></div>
          </dl>
        </article>
      ))}
    </div>
  );
};

export default AdminProductMobileList;
